import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RefreshCw, Pause, Clock } from 'lucide-react';
import { mmsPausedJobs, mmsJobResume } from '../../../services/mmsApi';

const PausedJobs = () => {
  const navigate = useNavigate();
  const [pausedJobs, setPausedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeNotes, setResumeNotes] = useState('');
  const [resumeImage, setResumeImage] = useState(null);
  const [resumeImagePreview, setResumeImagePreview] = useState(null);
  const [resuming, setResuming] = useState(false);
  const resumeImageInputRef = React.useRef(null);

  useEffect(() => {
    fetchPausedJobs();
  }, []);

  const fetchPausedJobs = async () => {
    try {
      setLoading(true);
      const response = await mmsPausedJobs();
      if (response.data.status === 1) {
        setPausedJobs(response.data.data?.paused_jobs || []);
      }
    } catch (error) {
      console.error('Error fetching paused jobs:', error);
      toast.error('Failed to load paused jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenResumeModal = (job) => {
    setSelectedJob(job);
    setShowResumeModal(true);
    setResumeNotes('');
    setResumeImage(null);
    setResumeImagePreview(null);
  };

  const handleResumeImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setResumeImage(file);
    setResumeImagePreview(URL.createObjectURL(file));
  };

  const handleResumeJob = async () => {
    if (!selectedJob) return;

    try {
      setResuming(true);
      await mmsJobResume(selectedJob.assignment_id, resumeNotes, resumeImage);
      
      toast.success('Job resumed successfully!');
      setShowResumeModal(false);
      setSelectedJob(null);
      
      // Refresh the list
      await fetchPausedJobs();
    } catch (error) {
      console.error('Error resuming job:', error);
      toast.error(error.response?.data?.message || 'Failed to resume job');
    } finally {
      setResuming(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    const lower = (status || '').toLowerCase();
    if (lower === 'paused') return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-ZA', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const formatDistance = (lat, lng) => {
    if (!lat || !lng) return 'Location unavailable';
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" style={{ borderTopColor: 'transparent', borderRight: '3px solid currentColor', borderBottom: '3px solid currentColor', borderLeft: '3px solid currentColor', borderRadius: '50%' }}></div>
              <p className="text-gray-600 font-medium">Loading paused jobs...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Professional Header Card */}
      <div className="container mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description, Results */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)' }}>
                <Pause className="h-5 w-5" style={{ color: '#EF4444' }} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Paused Jobs</h1>
                <p className="text-xs text-gray-500 mb-1">
                  Manage and resume your paused job assignments
                </p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#EF4444' }}>
                    {pausedJobs.length} {pausedJobs.length === 1 ? 'job' : 'jobs'} paused
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: Refresh Button */}
            <div className="flex items-center gap-2">
              <button
                onClick={fetchPausedJobs}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 pb-8">
        {pausedJobs.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Paused Jobs</h3>
            <p className="text-gray-600">You don't have any paused jobs at the moment.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pausedJobs.map((job) => (
              <div key={job.assignment_id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {job.jobcard_no || `Job #${job.assignment_id}`}
                    </h3>
                    {job.application_id && (
                      <p className="text-sm text-gray-600 mt-1">App #{job.application_id}</p>
                    )}
                  </div>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusBadgeColor(job.status)}`}>
                    {job.status}
                  </span>
                </div>

                {/* Job Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Property Address */}
                  {job.property_address && (
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 font-medium uppercase">Location</p>
                        <p className="text-sm text-gray-900 break-words">{job.property_address}</p>
                      </div>
                    </div>
                  )}

                  {/* Worker Info */}
                  {job.worker_name && (
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 font-medium uppercase">Assigned To</p>
                        <p className="text-sm text-gray-900">{job.worker_name}</p>
                        {job.worker_phone && (
                          <p className="text-xs text-gray-600">{job.worker_phone}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Pause Reason */}
                  {job.reason_name && (
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 font-medium uppercase">Pause Reason</p>
                        <p className="text-sm font-medium text-red-700">{job.reason_name}</p>
                        {job.reason_description && (
                          <p className="text-xs text-gray-600">{job.reason_description}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Paused Time */}
                  {job.paused_at && (
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1"> 
                        <p className="text-xs text-gray-500 font-medium uppercase">Paused</p>
                        <p className="text-sm text-gray-900">{formatDate(job.paused_at)}</p>
                        <button
                        onClick={() => handleOpenResumeModal(job)}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Resume Job
                    </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Pause Notes */}
                {job.pause_notes && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs text-red-600 font-medium uppercase mb-1">Additional Notes</p>
                    <p className="text-sm text-red-900">{job.pause_notes}</p>
                  </div>
                )}

                {/* Attached Image */}
                {job.image_path && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 font-medium uppercase mb-2">Attached Image</p>
                    <a
                      href={`/${job.image_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                    >
                      View Image
                    </a>
                  </div>
                )} 
                
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Resume Modal */}
      {showResumeModal && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Resume Job</h3>
            <p className="text-sm text-gray-600 mb-4">
              {selectedJob.jobcard_no || `Job #${selectedJob.assignment_id}`}
            </p>

            {/* Resume Notes */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resume Notes (optional)
              </label>
              <textarea
                value={resumeNotes}
                onChange={(e) => setResumeNotes(e.target.value)}
                placeholder="Any notes about resuming work..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                disabled={resuming}
              />
            </div>

            {/* Image Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attach Evidence Image (optional)
              </label>
              {resumeImagePreview ? (
                <div className="relative mb-2">
                  <img
                    src={resumeImagePreview}
                    alt="Preview"
                    className="w-full h-40 object-cover rounded-lg border border-gray-300"
                  />
                  <button
                    onClick={() => {
                      setResumeImage(null);
                      setResumeImagePreview(null);
                    }}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    disabled={resuming}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => resumeImageInputRef.current?.click()}
                  disabled={resuming}
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg py-4 text-center hover:border-blue-500 transition-colors disabled:opacity-50"
                >
                  <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <p className="text-sm text-gray-600">Click to upload image</p>
                </button>
              )}
              <input
                ref={resumeImageInputRef}
                type="file"
                accept="image/*"
                onChange={handleResumeImageChange}
                className="hidden"
                disabled={resuming}
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowResumeModal(false);
                  setSelectedJob(null);
                  setResumeNotes('');
                  setResumeImage(null);
                  setResumeImagePreview(null);
                }}
                disabled={resuming}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleResumeJob}
                disabled={resuming}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resuming ? 'Resuming...' : 'Resume Job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PausedJobs;
